'use no memo';

import type { OnChangeFn, Row, RowSelectionState, Table } from '@tanstack/react-table';
import type { MouseEvent } from 'react';

import { useRef } from 'react';

export interface UseTableRowSelectionProps<TData> {
  table: Table<TData>;
  setRowSelection: OnChangeFn<RowSelectionState>;
}

export function useTableRowSelection<TData>({ table, setRowSelection }: UseTableRowSelectionProps<TData>) {
  const lastSelectedRowIndexRef = useRef<number | null>(null);

  const handleRowClick = (event: MouseEvent, row: Row<TData>, rowIndex: number) => {
    const target = event.target as HTMLElement;
    if (target.closest('button') || target.closest('input') || target.closest('[role="button"]') || target.closest('a')) {
      return;
    }

    const isCtrlPressed = event.ctrlKey || event.metaKey;
    const isShiftPressed = event.shiftKey;

    if (isShiftPressed && lastSelectedRowIndexRef.current !== null) {
      // Shift+Click: Select range
      event.preventDefault(); // Prevent text selection only for Shift+Click
      const start = Math.min(lastSelectedRowIndexRef.current, rowIndex);
      const end = Math.max(lastSelectedRowIndexRef.current, rowIndex);
      const rows = table.getRowModel().rows;

      setRowSelection((prev) => {
        const newSelection = { ...prev };
        // Select all rows in the range
        for (let i = start; i <= end; i++) {
          const rowId = rows[i]?.id;
          if (rowId) {
            newSelection[rowId] = true;
          }
        }
        return newSelection;
      });
    } else if (isCtrlPressed) {
      // Ctrl+Click: Toggle selection
      row.toggleSelected();
      lastSelectedRowIndexRef.current = rowIndex;
    } else {
      // Regular click: Select only this row
      table.resetRowSelection();
      row.toggleSelected(true);
      lastSelectedRowIndexRef.current = rowIndex;
    }
  };

  return {
    handleRowClick,
  };
}
